import * as React from 'react'
import {createPortal} from 'react-dom'

import {Boundary} from '@base-uikit/boundaries/Boundary'
import ScrollFix from '@base-uikit/scroll/ScrollFix'
import {ReactNodeReturnType} from '@base-uikit/types'

type PortalProps = {
  children?: React.ReactNode
  required?: boolean
}

type PortalResult = {
  Portal: (PProps) => ReactNodeReturnType
  PortalProvider: React.Provider<Element | null | undefined>
  usePortal: () => Element | null | undefined
}

export default function createPortalProvider<LayerProps>({
  displayName,
  defaultContainer,
  Layer = 'span',
}: {
  displayName: string
  defaultContainer?: Element
  Layer?: string | React.ComponentType<LayerProps>
}): PortalResult {
  const Context = React.createContext<Element | null | undefined>(
    defaultContainer
  )
  Context.displayName = displayName
  const usePortal = () => React.useContext(Context)
  const Portal = ({children, required, ...props}: PortalProps) => {
    const container = usePortal()
    if (required && container === undefined) {
      throw new Error(
        `Portal "${displayName}" is not available in this context`
      )
    }
    if (container === null) {
      // waiting for the container to mount
      return null
    }
    // If container is not provided, we don't create a new layer - we render children in-place,
    // as if there was no <Portal> used. This makes it easy to disable all portals in testing and guides.
    return container
      ? createPortal(
          <ScrollFix>
            <Boundary boundaryName="Portal">
              {/* @ts-expect-error This is super hard to type, and "juice is not worth the squeeze™"... */}
              <Layer data-reactroot {...props}>
                {children}
              </Layer>
            </Boundary>
          </ScrollFix>,
          container
        )
      : children
  }
  Portal.displayName = `${displayName}Portal`

  return {Portal, PortalProvider: Context.Provider, usePortal}
}
